import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { chatWithAnalytics, fetchAnalyticsBriefing } from 'state/actions/analytic';
import { Bar, Line } from 'react-chartjs-2';
import { marked } from 'marked';
import './AnalyticsChatbot.css';

// --- Inline SVG Icons for a premium look ---
const SparkleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
  </svg>
);

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"></line>
    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const ChatIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
);

const ChartMessage = ({ config, msgId }) => {
  const containerRef = useRef(null);
  const exportRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest(`.chart-menu-${msgId}`)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen, msgId]);

  if (!config || !config.data || !config.chartType) return null;

  const labels = config.data.map(d => d.name || d.date || '');
  const data = config.data.map(d => d.value || d.count || d.total || 0);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Interactions',
        data,
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
        fill: config.chartType === 'line' ? false : undefined,
      }
    ]
  };

  const options = {
    maintainAspectRatio: false,
    scales: {
      yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
      xAxes: [{ ticks: { autoSkip: true, maxTicksLimit: 7 } }]
    },
    legend: { display: false },
    plugins: {
      datalabels: {
        display: false
      }
    }
  };

  const generateTempCanvas = () => {
    if (!exportRef.current) return null;
    const canvas = exportRef.current.querySelector('canvas');
    if (!canvas) return null;

    const dpr = window.devicePixelRatio || 1;
    
    // Layout in logical pixels
    const logicalPad = 40;
    const logicalTitleArea = 86;
    const logicalFooterArea = 48;

    const pad = logicalPad * dpr;
    const titleArea = logicalTitleArea * dpr;
    const footerArea = logicalFooterArea * dpr;

    const cw = canvas.width;
    const ch = canvas.height;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = cw + (pad * 2);
    tempCanvas.height = ch + pad + titleArea + footerArea;
    const ctx = tempCanvas.getContext('2d');

    // 1. Outer background (subtle app background)
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

    // 2. Inner card with shadow
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = 'rgba(15, 23, 42, 0.08)';
    ctx.shadowBlur = 24 * dpr;
    ctx.shadowOffsetY = 8 * dpr;
    
    // Draw rounded rectangle for the card
    const cardX = pad / 2;
    const cardY = pad / 2;
    const cardW = tempCanvas.width - pad;
    const cardH = tempCanvas.height - pad;
    const radius = 16 * dpr;
    
    ctx.beginPath();
    if (ctx.roundRect) {
      ctx.roundRect(cardX, cardY, cardW, cardH, radius);
    } else {
      ctx.rect(cardX, cardY, cardW, cardH);
    }
    ctx.fill();
    ctx.shadowColor = 'transparent'; // reset shadow

    // Card border
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1 * dpr;
    ctx.stroke();

    // 3. Draw Title
    ctx.fillStyle = '#0f172a';
    ctx.font = `bold ${24 * dpr}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText('Analytics Insight', pad, pad);

    // 4. Draw Date/Time
    ctx.fillStyle = '#64748b';
    ctx.font = `500 ${14 * dpr}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    const dateStr = new Date().toLocaleString([], { dateStyle: 'long', timeStyle: 'short' });
    ctx.fillText(`Report generated on ${dateStr}`, pad, pad + (34 * dpr));

    // 5. Draw divider line
    ctx.strokeStyle = '#f1f5f9';
    ctx.lineWidth = 2 * dpr;
    ctx.beginPath();
    ctx.moveTo(pad, pad + titleArea - (20 * dpr));
    ctx.lineTo(tempCanvas.width - pad, pad + titleArea - (20 * dpr));
    ctx.stroke();

    // 6. Draw actual chart
    ctx.drawImage(canvas, pad, pad + titleArea, cw, ch);

    // 7. Draw Watermark
    ctx.fillStyle = '#94a3b8';
    ctx.font = `bold ${13 * dpr}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillText('AdSign Analytics', tempCanvas.width - pad, tempCanvas.height - pad);

    return tempCanvas;
  };

  const handleDownload = () => {
    const tempCanvas = generateTempCanvas();
    if (tempCanvas) {
      const url = tempCanvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.download = `adsign-insight-${Date.now()}.png`;
      link.href = url;
      link.click();
    }
    setMenuOpen(false);
  };

  const handleOpenNewTab = () => {
    const tempCanvas = generateTempCanvas();
    if (tempCanvas) {
      const dataUrl = tempCanvas.toDataURL('image/png', 1.0);
      const newTab = window.open();
      if (newTab) {
        newTab.document.body.style.margin = '0';
        newTab.document.body.style.display = 'flex';
        newTab.document.body.style.justifyContent = 'center';
        newTab.document.body.style.alignItems = 'center';
        newTab.document.body.style.minHeight = '100vh';
        newTab.document.body.style.background = '#f1f5f9';
        
        const img = newTab.document.createElement('img');
        img.src = dataUrl;
        img.style.maxWidth = '90%';
        img.style.maxHeight = '90vh';
        img.style.objectFit = 'contain';
        img.style.boxShadow = '0 20px 40px rgba(0,0,0,0.1)';
        img.style.borderRadius = '12px';
        
        newTab.document.body.appendChild(img);
        newTab.document.title = 'Analytics Insight';
      }
    }
    setMenuOpen(false);
  };

  const handleDownloadCSV = () => {
    if (!config || !config.data || config.data.length === 0) return;
    
    // Use first object's keys as headers
    const keys = Object.keys(config.data[0]);
    
    // Build CSV string
    const csvContent = [
      keys.join(','),
      ...config.data.map(row => keys.map(k => `"${String(row[k]).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `adsign-data-${Date.now()}.csv`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
    
    setMenuOpen(false);
  };

  return (
    <div ref={containerRef} className="chatbot-chart-container" style={{ position: 'relative', marginTop: '16px' }}>
      
      <div className={`chart-menu-${msgId}`} style={{ position: 'absolute', top: '4px', right: '4px', zIndex: 10 }}>
        <button 
          onClick={() => setMenuOpen(!menuOpen)}
          className="chatbot-chart-menu-btn"
          title="Chart Options"
          aria-label="Chart Options"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="1"></circle>
            <circle cx="12" cy="5" r="1"></circle>
            <circle cx="12" cy="19" r="1"></circle>
          </svg>
        </button>
        
        {menuOpen && (
          <div className="chatbot-chart-dropdown">
            <button onClick={handleOpenNewTab}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
              Open in New Tab
            </button>
            <button onClick={handleDownload}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
              Download Image
            </button>
            <button onClick={handleDownloadCSV}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
              Download CSV
            </button>
          </div>
        )}
      </div>
      
      {/* Visible Chart for Chat UI */}
      <div style={{ height: '200px', width: '100%' }}>
        {config.chartType === 'line' ? (
          <Line data={chartData} options={options} />
        ) : (
          <Bar data={chartData} options={options} />
        )}
      </div>

      {/* Hidden Wide Chart strictly for Exporting (prevents squished labels) */}
      <div 
        ref={exportRef} 
        style={{ 
          position: 'absolute', 
          top: '-9999px', 
          left: '-9999px', 
          width: '800px', 
          height: '400px', 
          visibility: 'hidden',
          pointerEvents: 'none'
        }}
      >
        {config.chartType === 'line' ? (
          <Line data={chartData} options={{ ...options, animation: { duration: 0 } }} />
        ) : (
          <Bar data={chartData} options={{ ...options, animation: { duration: 0 } }} />
        )}
      </div>
    </div>
  );
};

// Function to generate context-aware suggestions
function getDynamicSuggestions(context) {
  const selectedType = context?.selectedType || 'poi';
  const availableTypes = context?.availableTypes || [];
  const typeNames = availableTypes.map(t => t.name);

  const now = new Date();
  const isStartOfMonth = now.getDate() <= 7;
  const isStartOfQuarter = [0, 3, 6, 9].includes(now.getMonth()) && isStartOfMonth;

  let typeLabel = "POI";
  if (selectedType === "place") typeLabel = "Place";
  else if (selectedType === "category") typeLabel = "Category";
  else if (selectedType === "leaderboard") typeLabel = "Screen";

  const suggestions = [];

  // Helper to get formatted plural label
  const getPluralLabel = (label) => {
    if (label === "POI") return "POIs";
    if (label === "Category") return "Categories";
    return `${label}s`;
  };

  // 1. Time-based suggestion
  if (isStartOfQuarter) {
    suggestions.push(`Summarize performance for last quarter.`);
  } else if (isStartOfMonth) {
    suggestions.push(`How did our ${getPluralLabel(typeLabel)} do last month?`);
  } else {
    suggestions.push(`Summarize this week's traffic.`);
  }

  // 2. Type-based suggestion
  suggestions.push(`Which ${typeLabel} had the most taps?`);

  // 3. Capability-based suggestion
  if (typeNames.includes("leaderboard") && selectedType !== "leaderboard") {
    suggestions.push(`Which screen is the most active?`);
  } else {
    suggestions.push(`What is our busiest day of the week?`);
  }

  return suggestions;
}

const AnalyticsChatbot = ({ context }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: "Hi! Ask me anything about your analytics — I can answer questions about your data or download reports for you. What would you like to know?",
      showSuggestions: true,
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const [briefingTrigger, setBriefingTrigger] = useState(0);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const briefingFetchedRef = useRef(false);
  const dispatch = useDispatch();

  // Scroll logic: default to top on open so digest is visible. Scroll to bottom on user interaction.
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        // If there are more than 2 messages (digest + greeting), or if a response is actively loading, scroll down.
        if (messages.length > 2 || loading) {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        } else if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = 0;
        }
      }, 50);
    }
  }, [messages, isOpen, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      // Delay slightly longer than the 300ms CSS animation to ensure focus stability
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus({ preventScroll: true });
        }
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Display the briefing when the chat opens
  useEffect(() => {
    if (!isOpen) return;

    const loadingId = 'briefing-loading';
    const digestPrefix = '📊 **Weekly Digest:**';
    const noDataText = '*No weekly digest available (no data recorded in the past 7 days).*';

    // Always show loading spinner first (strip any old digest so it re-animates)
    setMessages(prev => {
      let next = prev.filter(m => 
        m.id !== loadingId && 
        !m.text?.startsWith(digestPrefix) && 
        m.text !== noDataText
      );
      return [{ role: 'assistant', id: loadingId, isLoading: true }, ...next];
    });

    // Always wait a minimum delay for a smooth transition
    const MIN_DELAY = 1500;
    const startTime = Date.now();

    const showDigest = () => {
      const storageKey = `chatbot_briefing_${context?.siteId}`;
      const cached = localStorage.getItem(storageKey);

      if (cached) {
        try {
          const cachedObj = JSON.parse(cached);
          const ageHours = (Date.now() - (cachedObj.savedAt || 0)) / 36e5;

          if ((ageHours < 168 && cachedObj.text !== null) || (ageHours < 1 && cachedObj.text === null)) {
            // Mark as read
            if (cachedObj.isRead !== true) {
              cachedObj.isRead = true;
              localStorage.setItem(storageKey, JSON.stringify(cachedObj));
              setShowNudge(false);
            }

            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, MIN_DELAY - elapsed);

            setTimeout(() => {
              setMessages(prev => {
                // Remove loading bubble
                let next = prev.filter(m => m.id !== loadingId);
                // Don't add if already present
                const hasDigest = next.some(m => m.text?.startsWith(digestPrefix) || m.text === noDataText);
                if (hasDigest) return next;

                if (cachedObj.text) {
                  const exactText = `${digestPrefix}\n\n${cachedObj.text}`;
                  return [{ role: 'assistant', text: exactText }, ...next];
                } else {
                  return [{ role: 'assistant', text: noDataText }, ...next];
                }
              });
            }, remaining);
            return;
          }
        } catch (_) {}
      }

      // Data not ready yet — check again shortly
      setTimeout(showDigest, 300);
    };

    showDigest();
  }, [isOpen, briefingTrigger, context?.siteId]);

  // Reset or load chat history when site context changes
  useEffect(() => {
    if (!context?.siteId) return;

    // ── 1. Reset chat messages for this site ──
    const storageKey = `chatbot_messages_${context.siteId}`;
    const cached = localStorage.getItem(storageKey);
    let loaded = false;

    if (cached) {
      try {
        const { savedMessages, savedAt } = JSON.parse(cached);
        const ageMinutes = (Date.now() - savedAt) / 60000;
        if (ageMinutes < 30) {
          // Strip any old digest/loading messages so they don't interfere with re-generation
          const cleanMessages = savedMessages.filter(m => 
            m.id !== 'briefing-loading' && 
            !m.text?.startsWith('📊 **Weekly Digest:') && 
            m.text !== '*No weekly digest available (no data recorded in the past 7 days).*'
          );
          setMessages(cleanMessages);
          loaded = true;
        } else {
          localStorage.removeItem(storageKey);
        }
      } catch (e) {}
    }

    if (!loaded) {
      setMessages([
        {
          role: 'assistant',
          text: "Hi! I'm your AdSign Assistant. I can help you analyse traffic trends, generate charts and graphs, download reports, or even track unit history and tenant churn. What can I help you with today?",
          showSuggestions: true,
        },
      ]);
    }
    
    // ── 2. Always fetch a fresh briefing for this site ──
    // We always re-fetch on site change to guarantee accuracy.
    // A valid cache (< 7 days, has data) is used for the nudge dot only.
    const briefingKey = `chatbot_briefing_${context.siteId}`;
    const cachedBriefing = localStorage.getItem(briefingKey);
    let cachedObj = null;
    let cacheIsValid = false;

    if (cachedBriefing) {
      try {
        cachedObj = JSON.parse(cachedBriefing);
        const ageHours = (Date.now() - (cachedObj.savedAt || 0)) / 36e5;
        cacheIsValid = ageHours < 168 && cachedObj.text !== null;
      } catch (e) {}
    }

    if (cacheIsValid) {
      // Cache is fresh — show nudge if unread, and trigger display
      if (cachedObj.isRead === false) {
        setShowNudge(true);
      }
      setBriefingTrigger(Date.now());
    } else {
      // No valid cache — fetch in the background
      setShowNudge(false);
      dispatch(fetchAnalyticsBriefing(context, 'week')).then((result) => {
        const text = result?.briefing || null;
        localStorage.setItem(briefingKey, JSON.stringify({ text, savedAt: Date.now(), isRead: false }));
        if (text !== null) {
          setShowNudge(true);
        }
        setBriefingTrigger(Date.now());
      });
    }

  }, [context?.siteId]);

  // Save chat history to localStorage whenever it updates
  useEffect(() => {
    if (!context?.siteId || messages.length === 0) return;
    
    // Don't save if it's just the default greeting
    if (messages.length === 1 && messages[0].showSuggestions) {
      // We can remove it to keep storage clean if they cleared it
      localStorage.removeItem(`chatbot_messages_${context.siteId}`);
      return;
    }

    const storageKey = `chatbot_messages_${context.siteId}`;
    localStorage.setItem(storageKey, JSON.stringify({
      savedMessages: messages,
      savedAt: Date.now()
    }));
  }, [messages, context?.siteId]);

  // Refocus input after loading finishes so user can type again immediately
  useEffect(() => {
    if (!loading && isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [loading, isOpen]);

  // Animated close: play shrink animation, then unmount
  const closeChat = () => {
    if (isClosing) return;
    setIsExpanded(false);
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 250); // matches chatbot-out animation duration
  };

  // Escape key: expanded → collapse, compact → close chat
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (isExpanded) {
          setIsExpanded(false);
        } else if (isOpen) {
          closeChat();
        }
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [isOpen, isExpanded]);

  const sendMessage = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;

    setInput('');
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    const userMsgId = Date.now();
    setMessages((prev) => [...prev, { id: userMsgId, role: 'user', text: trimmed }]);
    setLoading(true);

    // Construct history for OpenAI (including the new message)
    const history = [
      ...messages
        .filter((m) => m.role !== 'system' && !m.showSuggestions)
        .map((m) => ({
          role: m.role,
          content: m.text,
        })),
      { role: 'user', content: trimmed },
    ];

    const result = await dispatch(chatWithAnalytics(history, context));
    const assistantMsgId = Date.now() + 1;

    const newMessage = {
      id: assistantMsgId,
      role: 'assistant',
      text: result?.reply ?? 'Something went wrong. Please try again.',
      action: result?.action,
      downloadStatus: result?.downloadPromise ? 'loading' : undefined,
    };

    setMessages((prev) => [...prev, newMessage]);
    setLoading(false);

    // If there's a download in progress, wait for it and update the bubble
    if (result?.downloadPromise) {
      try {
        const downloadAction = await result.downloadPromise;
        // Check if the action dispatched was success
        if (downloadAction && downloadAction.type.includes('SUCCESS')) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsgId ? { ...m, downloadStatus: 'success' } : m
            )
          );
        } else {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsgId ? { ...m, downloadStatus: 'error' } : m
            )
          );
        }
      } catch (e) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId ? { ...m, downloadStatus: 'error' } : m
          )
        );
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const parseMarkdown = (text) => {
    if (!text) return '';

    // Pre-processing: Clean up common AI formatting artifacts
    const cleanedText = text
      // Remove blank line after a colon (handles labels that might be bolded)
      .replace(/:\s*(\*\*)?\s*\n\s*\n+/g, ':$1\n');

    // Use marked library for professional parsing
    return marked.parse(cleanedText, {
      breaks: true, // adds <br> on single line breaks
      gfm: true,    // GitHub Flavored Markdown
    });
  };

  const renderBubbleContent = (msg) => {
    if (msg.isLoading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }}>
          <span className="chatbot-spinner" /> Generating your weekly digest...
        </div>
      );
    }

    let textToRender = msg.text || '';
    let chartConfig = null;

    const jsonBlockRegex = /```json\s*([\s\S]*?)\s*```/;
    const match = textToRender.match(jsonBlockRegex);
    if (match) {
      try {
        chartConfig = JSON.parse(match[1]);
        textToRender = textToRender.replace(jsonBlockRegex, '').trim();
      } catch (e) {
        console.error("Failed to parse chart JSON", e);
      }
    }

    return (
      <>
        <div 
          className="chatbot-markdown-content" 
          dangerouslySetInnerHTML={{ __html: parseMarkdown(textToRender) }} 
        />

        {chartConfig && <ChartMessage config={chartConfig} msgId={msg.id} />}

        {/* Suggestion chips on first message */}
      {msg.showSuggestions && (
        <div className="chatbot-suggestions">
          {getDynamicSuggestions(context).map((s) => (
            <button
              key={s}
              className="chatbot-suggestion-chip"
              onClick={() => sendMessage(s)}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Download Status Badges */}
      {msg.action === 'download_analytics' && (
        <>
          {msg.downloadStatus === 'loading' && (
            <div className="chatbot-download-badge chatbot-download-badge--loading">
              <span className="chatbot-spinner" />
              Preparing download...
            </div>
          )}
          {msg.downloadStatus === 'success' && (
            <div className="chatbot-download-badge chatbot-download-badge--success">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
              Download started
            </div>
          )}
          {msg.downloadStatus === 'error' && (
            <div className="chatbot-download-badge chatbot-download-badge--error">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
              Download failed
            </div>
          )}
        </>
      )}

      {/* Error badge (system level) */}
      {msg.action === 'error' && (
        <div className="chatbot-error-badge">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
          Something went wrong
        </div>
      )}
    </>
    );
  };

  return (
    <div className="chatbot-container">
      {/* ── Backdrop overlay for expanded mode ── */}
      {isOpen && isExpanded && (
        <div 
          className="chatbot-backdrop" 
          onClick={() => setIsExpanded(false)} 
        />
      )}

      {/* ── Chat panel ── */}
      {(isOpen || isClosing) && (
        <div className={`chatbot-panel ${isExpanded ? 'chatbot-panel--expanded' : ''} ${isClosing ? 'chatbot-panel--closing' : ''}`}>
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-left">
              <div className="chatbot-avatar">
                <SparkleIcon />
              </div>
              <div className="chatbot-header-text">
                <div className="chatbot-title">Analytics Assistant</div>
                <div className="chatbot-subtitle">Powered by OpenAI</div>
              </div>
            </div>
            <div className="chatbot-header-actions">
              <button
                className="chatbot-header-close"
                onClick={() => setIsExpanded(e => !e)}
                title={isExpanded ? 'Collapse' : 'Expand'}
                aria-label={isExpanded ? 'Collapse chat' : 'Expand chat'}
              >
                {isExpanded ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 14 10 14 10 20"></polyline><polyline points="20 10 14 10 14 4"></polyline><line x1="14" y1="10" x2="21" y2="3"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg>
                )}
              </button>
              <button
                className="chatbot-header-close"
                onClick={() => {
                  if (context?.siteId) {
                    localStorage.removeItem(`chatbot_messages_${context.siteId}`);
                    localStorage.removeItem(`chatbot_briefing_${context.siteId}`);
                    dispatch(fetchAnalyticsBriefing(context, 'week')).then((result) => {
                      const text = result?.briefing || null;
                      localStorage.setItem(
                        `chatbot_briefing_${context.siteId}`,
                        JSON.stringify({ text, savedAt: Date.now(), isRead: false })
                      );
                      setBriefingTrigger(Date.now());
                    });
                  }
                  setMessages([
                    {
                      role: 'assistant',
                      text: "Hi! I'm your AdSign Assistant. I can help you analyse traffic trends, generate charts and graphs, download reports, or even track unit history and tenant churn. What can I help you with today?", 
                      showSuggestions: true,
                    },
                  ]);
                }}
                title="Clear Chat"
                aria-label="Clear chat history"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="chatbot-messages" ref={messagesContainerRef}>
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`chatbot-msg chatbot-msg--${msg.role}`}
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className="chatbot-bubble">
                  {renderBubbleContent(msg)}
                </div>
              </div>
            ))}

            {/* Loading dots */}
            {loading && (
              <div className="chatbot-msg chatbot-msg--assistant">
                <div className="chatbot-bubble chatbot-bubble--loading">
                  <span className="chatbot-dot" />
                  <span className="chatbot-dot" />
                  <span className="chatbot-dot" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="chatbot-input-area">
            <div className="chatbot-input-wrapper">
              <textarea
                ref={inputRef}
                autoFocus
                className="chatbot-input"
                value={input}
                rows={1}
                onChange={(e) => {
                  setInput(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything..."
                aria-label="Chat message"
                autoComplete="off"
              />
              <button
                className="chatbot-send-btn"
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                aria-label="Send message"
              >
                <SendIcon />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── FAB trigger ── */}
      <div style={{ position: 'relative' }}>
        {showNudge && !isOpen && (
          <div className="chatbot-nudge-tooltip">
            📊 New weekly summary available
          </div>
        )}
        <button
          className={`chatbot-trigger ${isOpen ? 'chatbot-trigger--active' : ''}`}
          onClick={() => {
            if (isOpen) {
              closeChat();
            } else {
              setShowNudge(false);
              setIsOpen(true);
            }
          }}
          aria-label={isOpen ? 'Close analytics assistant' : 'Open analytics assistant'}
          title="Analytics Assistant"
        >
          <div className="chatbot-trigger-icon">
            {isOpen ? <CloseIcon /> : <ChatIcon />}
          </div>
          {showNudge && !isOpen && <div className="chatbot-nudge-badge" />}
        </button>
      </div>
    </div>
  );
};

export default AnalyticsChatbot;